import isString from 'lodash/isString';
import transform from 'lodash/transform';

export function convertEmptyStringToNull(value?: string | null): string | null {
  return !value || value.trim() === '' ? null : value;
}

export const isHTTPS = (value: string) => {
  if (!value) return true;
  return /^https:\/\//.test(value);
};

export function toKebabCase(str: string) {
  return str
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();
}

/**
 * Checks if a string is a valid JSON string (object or array)
 * @param str - The string to check
 * @returns true if the string appears to be JSON, false otherwise
 */
export function isJsonString(str: string): boolean {
  if (!isString(str) || str.length < 2) return false;
  const trimmed = str.trim();
  return (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
}

/**
 * Parses nested JSON strings within an object's values
 * @param obj - Object containing potentially stringified JSON values
 * @returns Object with parsed JSON values where applicable
 */
export function parseNestedJsonStrings(obj: Record<string, any>): Record<string, any> {
  return transform(
    obj,
    (result, value, key) => {
      if (isString(value) && isJsonString(value)) {
        try {
          result[key] = JSON.parse(value);
        } catch {
          result[key] = value; // Keep original if parsing fails
        }
      } else {
        result[key] = value;
      }
    },
    {} as Record<string, any>,
  );
}

/**
 * Checks if a string contains HTML content
 * @param str - The string to check
 * @returns true if the string appears to contain HTML, false otherwise
 */
/**
 * Regex for parsing ISO 8601 duration strings (e.g., "P1Y", "P2M", "P30D", "P1Y6M")
 * Captures optional year, month, and day components
 */
export const ISO_8601_DURATION_REGEX = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?/;

/**
 * Formats an ISO 8601 duration string to a tag-friendly format
 * Only returns the largest unit present (e.g., "P1Y6M" returns "1-Year")
 * @param validity ISO 8601 duration string (e.g., "P1Y", "P30D")
 * @returns Formatted string for tag (e.g., "1-Year", "30-Day") or empty string if invalid
 */
export function formatWarrantyDurationForTag(validity: string): string {
  const matches = validity.match(ISO_8601_DURATION_REGEX);

  if (!matches) return '';

  const [, yearsStr, monthsStr, daysStr] = matches;
  const years = parseInt(yearsStr || '0', 10);
  const months = parseInt(monthsStr || '0', 10);
  const days = parseInt(daysStr || '0', 10);

  if (years > 0) {
    return `${years}-Year`;
  } else if (months > 0) {
    return `${months}-Month`;
  } else if (days > 0) {
    return `${days}-Day`;
  }

  return '';
}

export function isHtml(str: string): boolean {
  if (!isString(str) || str.length < 3) return false;

  // Common HTML patterns to check for
  const htmlPatterns = [
    /<[a-z][\s\S]*?>/i, // HTML tags (non-greedy to avoid matching incomplete tags)
    /&[a-z]+;/i, // HTML entities
    /&#\d+;/, // Decimal numeric HTML entities
    /&#x[0-9a-f]+;/i, // Hexadecimal numeric HTML entities
    /<![^>]*>/, // HTML comments
    /<!\[CDATA\[[\s\S]*?\]\]>/, // CDATA sections
    /<\?[^>]*\?>/, // XML processing instructions
    /<![a-z][^>]*>/i, // DOCTYPE and other declarations
  ];

  return htmlPatterns.some(pattern => pattern.test(str));
}

/**
 * Masks an email address for PII protection in logs
 * @param email - The email address to mask
 * @returns Masked email in format "e***@domain.com"
 */
export function maskEmail(email: string): string {
  if (!email) return '***@***';
  const [user, domain] = email.split('@');
  return `${user?.[0] ?? ''}***@${domain ?? ''}`;
}
